/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {ChangeEvent, memo} from 'react';
import {makeStyles} from '@material-ui/core';
import LineChart from '@ion/charting/src/line/LineChart';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {GenericObject, SensorUnits} from '@ion/core/src/types/entities';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {GenericSensorTooltip} from '../reports/GenericSensorTooltip';
import useChartOptions from './hooks/useChartOptions';
import {capitalizeSensor, populateDropdownOptions} from './utils/helpers';

interface GenericSensorLineChartProps {
  data: GenericObject;
  chartData: GenericSensorData;
  sensorType: string;
  onSensorTypeChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  isDialogRender?: boolean;
};

export type GenericSensorData = {
  id: string;
  data: GenericObject[];
  threshold: GenericObject;
};

const useStyles = makeStyles(() => ({
  detailWrapper : {
    height : '100%'
  },
  selectionContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center',
    padding        : '0 15px'
  },
  // the card's own 16px body padding (ION-7461) already insets this chart -
  // drops the legacy side padding so the selector/reading row aligns to the
  // 424px content width instead of doubling up ...
  currentSelectionContainer : {
    padding : 0
  },
  dropdown : {
    maxWidth : 300,
    width    : '100%'
  },
  currentDropdown : {
    maxWidth : '100%'
  },
  currentStatus : {
    color     : dlsColor('lightTeal'),
    fontSize  : '20px',
    minWidth  : '105px',
    textAlign : 'right'
  },
  currentTime : {
    ...dlsAlt('typography', 'label3'),
    color          : dlsColor('grey3'),
    fontWeight     : 700,
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    padding        : '15px'
  },
  // overrides `currentTime`'s legacy 15px side padding to align the
  // timestamp to the 424px content width - keeps its 15px top/bottom
  // spacing ...
  currentCurrentTime : {
    padding : '15px 0'
  }
}));

/**
 * @GenericSensorLineChart
 *
 * This component renders the generic sensor chart.
 *
 * ```typescript
 *
 * import {GenericSensorLineChart} from '@ion/dls/src/routes/tracking/sc';
 *
 *
 *  <GenericSensorLineChart data={data}
 *  chartData={chartData}
 *  sensorType={sensorType}
 *  onSensorTypeChange={onSensorTypeChange}
 *  isDialogRender={isShowExpandModal}/>
 *
 * ```
 */
export const GenericSensorLineChart = memo(({
  data,
  chartData,
  sensorType,
  onSensorTypeChange,
  isDialogRender = false
}: GenericSensorLineChartProps) => {
  const classes                   = useStyles();
  const isCurrent                 = isCurrentTheme();
  const {eventTime, units, value} = chartData?.data?.[0] || {};
  const preferredTimeFormat       = UserPreferences.getSystemTimeFormat();
  const isTemperature             = units === SensorUnits.F || units === SensorUnits.C;
  const {
    yScale,
    axisLeft,
    xScale,
    axisBottom,
    legendTheme,
    domainTheme
  } = useChartOptions(preferredTimeFormat);

  return (
    <div className={classes.detailWrapper}>
      <div className={themedClassName(classes.selectionContainer, classes.currentSelectionContainer, isCurrent)}>
        <div className={themedClassName(classes.dropdown, classes.currentDropdown, isCurrent)}>
          <Dropdown displaySelectedLabel
            options={populateDropdownOptions(data)}
            fieldLabel={null}
            placeholder={`Sensor 1 - ${capitalizeSensor(chartData?.id)}`}
            value={sensorType}
            name={'Sensor Type'}
            onChange={onSensorTypeChange}/>
        </div>
        <span className={classes.currentStatus}>
          {value}
          {isTemperature ? '°' : null} {units}
        </span>
      </div>
      <span className={themedClassName(classes.currentTime, classes.currentCurrentTime, isCurrent)}>
        {getFormattedDate(eventTime)}
      </span>
      <div style={{height: isDialogRender ? 350 : 250}}>
        <LineChart noDataLabel={translate('common:noData.noData')}
          enableSlices={false}
          loading={false}
          pointBorderWidth={0}
          getColor={dlsColor('lightTeal')}
          lineWidth={2}
          pointSize={10}
          enableGridX={false}
          axisTop={null}
          axisRight={null}
          curve={'monotoneY'}
          xScale={xScale as any}
          yScale={yScale as any}
          axisBottom={axisBottom as any}
          axisLeft={axisLeft as any}
          data={[chartData]}
          margin={{top: 24, right: 24, bottom: 60, left: 60}}
          renderTooltipLabel={GenericSensorTooltip}
          legendTheme={legendTheme}
          domainTheme={domainTheme}
          isInteractive
          enableArea
          useMesh
          animate />
      </div>
    </div>
  );
});

export default GenericSensorLineChart;
